import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { Plus, Stamp, Trash2 } from 'lucide-react';
import FormModalLayout from '../Common/FormModalLayout';
import { authService, licenseService } from '../../services/api';
import { userFacingError } from '../../i18n/userFacingError';
import { formToTemplate, templateFormError, templateToForm } from '../../utils/authorization';

async function loadTemplate(tipoId) {
  try {
    return await licenseService.getAuthorizationTemplate(tipoId);
  } catch (error) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
}

/** Modelo de autorização (AP, ASV) de um tipo de licença: série, validade, condicionantes e assinantes. */
const AuthorizationTemplateModal = ({ tipo, onClose }) => {
  const [form, setForm] = useState(() => templateToForm(null, tipo.nome));
  const { data: template, isLoading } = useQuery(['authorization-template', tipo.id], () => loadTemplate(tipo.id));
  const { data: usersRaw, error: usersError } = useQuery(['tenant-users'], () => authService.listUsers(), {
    retry: false,
  });
  const users = usersRaw?.data?.users || [];

  useEffect(() => {
    if (!isLoading) setForm(templateToForm(template, tipo.nome));
  }, [template, isLoading, tipo.nome]);

  const problem = templateFormError(form);
  const save = useMutation(() => licenseService.saveAuthorizationTemplate(tipo.id, formToTemplate(form)), {
    onSuccess: () => {
      toast.success('Modelo de autorização salvo.');
      onClose();
    },
    onError: (e) => toast.error(userFacingError(e, 'saveError')),
  });

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const setSigner = (index, key, value) =>
    setForm((prev) => ({
      ...prev,
      assinantes: prev.assinantes.map((s, i) => (i === index ? { ...s, [key]: value } : s)),
    }));
  const pickUser = (index, userId) => {
    const picked = users.find((u) => String(u.id) === userId);
    setForm((prev) => ({
      ...prev,
      assinantes: prev.assinantes.map((s, i) =>
        i === index ? { ...s, user_id: userId, nome: s.nome || picked?.username || '' } : s
      ),
    }));
  };

  return (
    <FormModalLayout
      title={`Modelo de autorização · ${tipo.nome}`}
      subtitle="O documento sai neste modelo e é numerado na série quando todos os assinantes assinarem."
      headerIcon={Stamp}
      onClose={onClose}
      size="2xl"
      footer={
        <>
          {problem ? <span className="mr-auto text-xs text-amber-700">{problem}</span> : null}
          <button type="button" className="btn btn-outline" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!!problem || save.isLoading}
            onClick={() => save.mutate()}
          >
            Salvar modelo
          </button>
        </>
      }
    >
      {isLoading ? (
        <p className="text-sm text-slate-500">Carregando…</p>
      ) : (
        <div className="form-stack">
          <div className="grid gap-3 sm:grid-cols-[1fr_120px]">
            <label className="form-group">
              <span className="form-label form-label-required">Título impresso</span>
              <input className="form-input" value={form.titulo} onChange={set('titulo')} />
            </label>
            <label className="form-group">
              <span className="form-label form-label-required">Sigla</span>
              <input className="form-input uppercase" value={form.sigla} onChange={set('sigla')} placeholder="AP" />
            </label>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="form-group">
              <span className="form-label form-label-required">Próximo número</span>
              <input type="number" min={1} className="form-input" value={form.proximo_numero} onChange={set('proximo_numero')} />
            </label>
            <label className="form-group">
              <span className="form-label form-label-required">Validade (dias)</span>
              <input type="number" min={1} className="form-input" value={form.validade_dias} onChange={set('validade_dias')} />
            </label>
            <label className="form-group">
              <span className="form-label">Local de emissão</span>
              <input className="form-input" value={form.local} onChange={set('local')} placeholder="Coruripe – AL" />
            </label>
          </div>
          <label className="form-group">
            <span className="form-label">Condicionantes padrão (uma por linha)</span>
            <textarea className="form-input min-h-[120px] text-sm" value={form.condicionantesTexto} onChange={set('condicionantesTexto')} />
          </label>

          <div>
            <span className="form-label form-label-required">Assinantes</span>
            {usersError ? (
              <p className="mb-2 text-xs text-amber-700">
                Não foi possível listar os usuários do órgão. Peça a quem administra usuários para configurar os assinantes.
              </p>
            ) : null}
            <ul className="space-y-2">
              {form.assinantes.map((signer, index) => (
                <li key={index} className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
                  <select
                    aria-label={`Usuário do assinante ${index + 1}`}
                    className="form-input"
                    value={signer.user_id}
                    onChange={(e) => pickUser(index, e.target.value)}
                  >
                    <option value="">Usuário…</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.username}
                      </option>
                    ))}
                  </select>
                  <input
                    aria-label={`Nome impresso do assinante ${index + 1}`}
                    className="form-input"
                    placeholder="Nome como sai no documento"
                    value={signer.nome}
                    onChange={(e) => setSigner(index, 'nome', e.target.value)}
                  />
                  <input
                    aria-label={`Cargo do assinante ${index + 1}`}
                    className="form-input"
                    placeholder="Cargo"
                    value={signer.cargo}
                    onChange={(e) => setSigner(index, 'cargo', e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-outline text-red-700"
                    aria-label={`Remover assinante ${index + 1}`}
                    disabled={form.assinantes.length === 1}
                    onClick={() =>
                      setForm((prev) => ({ ...prev, assinantes: prev.assinantes.filter((_, i) => i !== index) }))
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="mt-2 text-sm font-medium text-primary-700"
              onClick={() =>
                setForm((prev) => ({ ...prev, assinantes: [...prev.assinantes, { user_id: '', nome: '', cargo: '' }] }))
              }
            >
              <Plus className="mr-1 inline h-4 w-4" />
              Adicionar assinante
            </button>
          </div>
        </div>
      )}
    </FormModalLayout>
  );
};

export default AuthorizationTemplateModal;
